import React, { useState } from 'react';
import * as Accordion from '@radix-ui/react-accordion';
import './FmAccordion.css';

export interface FmAccordionItem {
  key: string;
  label: React.ReactNode;
  children: React.ReactNode;
  /** Secondary text on the trailing side of the header row. */
  extra?: React.ReactNode;
  'data-testid'?: string;
}

export interface FmAccordionProps {
  items: FmAccordionItem[];
  /** `single` keeps at most one panel open; `multiple` lets panels open independently. */
  type?: 'single' | 'multiple';
  /** Panels open on first render (uncontrolled). */
  defaultOpen?: string[];
  /** Open panels (controlled). */
  open?: string[];
  onOpenChange?: (openKeys: string[]) => void;
  /** Unmount closed panel bodies; use when a body is a large catalog. */
  lazy?: boolean;
  /** Borderless rows on the band background instead of a framed list. */
  plain?: boolean;
  /**
   * @internal DS-internal className prop; forbidden in route-owned code.
   */
  className?: string;
  'data-testid'?: string;
}

const cx = (...parts: Array<string | false | null | undefined>): string =>
  parts.filter(Boolean).join(' ');

/**
 * FmAccordion — disclosure list on Radix Accordion, which owns the button
 * semantics, aria-expanded/aria-controls wiring and arrow/Home/End focus
 * movement between headers.
 */
export const FmAccordion: React.FC<FmAccordionProps> = ({
  items,
  type = 'multiple',
  defaultOpen,
  open,
  onOpenChange,
  lazy = false,
  plain = false,
  className,
  'data-testid': dataTestId,
}) => {
  // The open keys are tracked here (controlled or not) so closed bodies that
  // stay mounted can carry `hidden` and leave the accessibility tree.
  const [uncontrolledOpen, setUncontrolledOpen] = useState<string[]>(defaultOpen ?? []);
  const openKeys = open ?? uncontrolledOpen;
  const commit = (keys: string[]): void => {
    if (open === undefined) setUncontrolledOpen(keys);
    onOpenChange?.(keys);
  };
  // Radix's single mode speaks one string with '' for "none"; the DS speaks key lists.
  const rootProps: Accordion.AccordionSingleProps | Accordion.AccordionMultipleProps =
    type === 'single'
      ? {
          type: 'single',
          collapsible: true,
          value: openKeys[0] ?? '',
          onValueChange: (value) => commit(value ? [value] : []),
        }
      : { type: 'multiple', value: openKeys, onValueChange: commit };
  return (
    <Accordion.Root
      {...rootProps}
      className={cx('fm-accordion', plain && 'fm-accordion--plain', className)}
      data-testid={dataTestId}
    >
      {items.map((item) => (
        <Accordion.Item
          key={item.key}
          value={item.key}
          className="fm-accordion__item"
          data-testid={item['data-testid']}
        >
          <Accordion.Header className="fm-accordion__header">
            <Accordion.Trigger className="fm-accordion__trigger">
              <span className="fm-accordion__caret" aria-hidden="true" />
              <span className="fm-accordion__label">{item.label}</span>
              {item.extra ? <span className="fm-accordion__extra">{item.extra}</span> : null}
            </Accordion.Trigger>
          </Accordion.Header>
          {/* Lazy items let Radix own `hidden`; passing it at all would
              override Radix's closed state and leave an empty padded strip. */}
          <Accordion.Content
            className="fm-accordion__content"
            {...(lazy ? {} : { forceMount: true, hidden: !openKeys.includes(item.key) })}
          >
            {item.children}
          </Accordion.Content>
        </Accordion.Item>
      ))}
    </Accordion.Root>
  );
};

FmAccordion.displayName = 'FmAccordion';
